/**
 * Visor de recorrido 360° tipo Matterport (three.js).
 * Lee una casa descrita en casa.json: cada punto es una foto 360° equirectangular (2:1) y sus
 * conexiones dicen hacia dónde (yaw, en grados dentro de la foto) está el siguiente cuarto.
 * yaw 0 = centro de la foto; positivo = hacia la derecha.
 */

export interface Conexion { a: string; yaw: number; distancia?: number; llegar?: number; }
export interface PuntoCasa {
	id: string; titulo: string; foto: string; mirar?: number; plano?: number[]; conexiones: Conexion[];
}
export interface Casa {
	nombre: string; ubicacion?: string; portada?: string; alturaCamara?: number; puntos: PuntoCasa[];
}

export interface OpcionesVisor {
	base: string;                 // carpeta de la casa, p. ej. assets/recorrido/casa-ejemplo/
	casa: Casa;
	cine?: boolean;               // banner: gira solo, sin controles
	alCambiar?: (p: PuntoCasa) => void;
	alCargar?: () => void;
}

const RADIO = 10;
const rad = (g: number) => (g * Math.PI) / 180;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const suave = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const difAng = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b));

let threePromesa: Promise<any> | null = null;
const cargarThree = () => (threePromesa = threePromesa || import('three'));

export class Visor360 {
	puntoActual!: PuntoCasa;
	enTransicion = false;

	private T: any;
	private renderer: any;
	private escena: any;
	private camara: any;
	private raycaster: any;
	private piso: any;
	private cursor: any;
	private actual: any;           // grupo: esfera + marcadores del punto actual
	private texturas = new Map<string, Promise<any>>();
	private raf = 0;
	private ultimo = 0;
	private vivo = true;
	private visible = true;

	private yaw = 0;               // dirección de la cámara en el mundo (rad)
	private pitch = 0;
	private velYaw = 0;
	private velPitch = 0;
	private fov = 75;
	private arrastre: { x: number; y: number; t: number; mov: number } | null = null;
	private mouse = { x: 0, y: 0, dentro: false };
	private quitar: (() => void)[] = [];
	private obsTam?: ResizeObserver;
	private obsVista?: IntersectionObserver;
	private etiqueta!: HTMLDivElement;
	private alto: number;

	constructor(private el: HTMLElement, private op: OpcionesVisor) {
		this.alto = op.casa.alturaCamara || 1.5;
	}

	async iniciar(idInicial?: string): Promise<void> {
		this.T = await cargarThree();
		if (!this.vivo) return;
		const T = this.T;

		this.renderer = new T.WebGLRenderer({ antialias: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
		const lienzo: HTMLCanvasElement = this.renderer.domElement;
		lienzo.style.cssText = 'display:block;width:100%;height:100%;outline:none;' + (this.op.cine ? '' : 'cursor:grab;touch-action:none;');
		this.el.appendChild(lienzo);

		this.escena = new T.Scene();
		this.camara = new T.PerspectiveCamera(this.fov, 1, 0.05, 100);
		this.raycaster = new T.Raycaster();
		this.piso = new T.Plane(new T.Vector3(0, 1, 0), this.alto);

		if (!this.op.cine) {
			this.cursor = this.crearAro(0.22, 0.28, 0xffffff, 0.9);
			this.cursor.visible = false;
			this.escena.add(this.cursor);
			this.etiqueta = document.createElement('div');
			this.etiqueta.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;opacity:0;transition:opacity .2s;' +
				'font:600 13px/1 sofiapro,system-ui,sans-serif;color:#fff;background:rgba(15,18,28,.72);padding:7px 12px;' +
				'border-radius:999px;white-space:nowrap;transform:translate(-50%,-150%);backdrop-filter:blur(6px);';
			this.el.appendChild(this.etiqueta);
			this.eventos(lienzo);
		}

		this.obsTam = new ResizeObserver(() => this.redimensionar());
		this.obsTam.observe(this.el);
		this.obsVista = new IntersectionObserver((e) => (this.visible = e[0].isIntersecting));
		this.obsVista.observe(this.el);
		this.redimensionar();

		const p = this.punto(idInicial || this.op.casa.portada || this.op.casa.puntos[0].id);
		const grupo = await this.crearGrupo(p);
		if (!this.vivo) return;
		grupo.rotation.y = 0;
		this.yaw = rad(p.mirar || 0);
		this.actual = grupo;
		this.puntoActual = p;
		this.escena.add(grupo);
		this.precargarVecinos(p);
		if (this.op.alCargar) this.op.alCargar();
		if (this.op.alCambiar) this.op.alCambiar(p);
		this.ultimo = performance.now();
		this.bucle();
	}

	destruir(): void {
		this.vivo = false;
		cancelAnimationFrame(this.raf);
		this.quitar.forEach((f) => f());
		if (this.obsTam) this.obsTam.disconnect();
		if (this.obsVista) this.obsVista.disconnect();
		this.texturas.forEach((t) => t.then((x: any) => x.dispose()).catch(() => { }));
		if (this.escena) this.escena.traverse((o: any) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); });
		if (this.renderer) { this.renderer.dispose(); this.renderer.domElement.remove(); }
		if (this.etiqueta) this.etiqueta.remove();
	}

	/** ir a un cuarto: si hay conexión directa se "camina", si no, fundido */
	irA(id: string): void {
		if (this.enTransicion || !this.puntoActual || id === this.puntoActual.id) return;
		const c = this.puntoActual.conexiones.find((x) => x.a === id);
		this.transicion(this.punto(id), c || null);
	}

	/* ------------------------------------------------------------------ */

	private punto(id: string): PuntoCasa {
		return this.op.casa.puntos.find((p) => p.id === id) || this.op.casa.puntos[0];
	}

	private textura(p: PuntoCasa): Promise<any> {
		if (!this.texturas.has(p.id)) {
			const T = this.T;
			this.texturas.set(p.id, new Promise((ok, mal) => new T.TextureLoader().load(this.op.base + p.foto, (t: any) => {
				t.colorSpace = T.SRGBColorSpace;
				t.minFilter = T.LinearFilter;
				t.generateMipmaps = false;
				t.anisotropy = 4;
				ok(t);
			}, undefined, mal)));
		}
		return this.texturas.get(p.id)!;
	}

	private precargarVecinos(p: PuntoCasa): void {
		p.conexiones.forEach((c) => this.textura(this.punto(c.a)).catch(() => { }));
	}

	private crearAro(r1: number, r2: number, color: number, op: number): any {
		const T = this.T;
		const g = new T.Group();
		const aro = new T.Mesh(new T.RingGeometry(r1, r2, 48), new T.MeshBasicMaterial({ color, transparent: true, opacity: op, depthTest: false }));
		const disco = new T.Mesh(new T.CircleGeometry(r1, 48), new T.MeshBasicMaterial({ color, transparent: true, opacity: op * 0.22, depthTest: false }));
		aro.renderOrder = disco.renderOrder = 5;
		g.add(aro, disco);
		g.rotation.x = -Math.PI / 2;
		return g;
	}

	/** dirección en coordenadas de la foto (yaw/pitch) -> vector */
	private dir(yaw: number, pitch = 0): any {
		return new this.T.Vector3(-Math.cos(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.sin(yaw) * Math.cos(pitch));
	}

	private async crearGrupo(p: PuntoCasa): Promise<any> {
		const T = this.T;
		const tex = await this.textura(p);
		const geo = new T.SphereGeometry(RADIO, 96, 64);
		geo.scale(-1, 1, 1);
		const esfera = new T.Mesh(geo, new T.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
		const g = new T.Group();
		g.add(esfera);
		g.userData = { esfera, marcadores: [] as any[] };
		if (!this.op.cine) {
			p.conexiones.forEach((c) => {
				const d = c.distancia || 2.5;
				const m = this.crearAro(0.26, 0.34, 0xffffff, 0.85);
				const v = this.dir(rad(c.yaw)).multiplyScalar(d);
				m.position.set(v.x, -this.alto + 0.01, v.z);
				m.userData = { conexion: c };
				m.children.forEach((h: any) => (h.userData = { conexion: c }));
				g.add(m);
				g.userData.marcadores.push(m);
			});
		}
		return g;
	}

	private opacidad(g: any, o: number): void {
		g.traverse((x: any) => {
			if (!x.material) return;
			if (x.userData.op0 === undefined) x.userData.op0 = x.material.opacity;
			x.material.opacity = x.userData.op0 * o;
		});
	}

	/** caminata: avanza hacia la conexión mientras la siguiente foto entra */
	private async transicion(destino: PuntoCasa, c: Conexion | null): Promise<void> {
		this.enTransicion = true;
		if (this.cursor) this.cursor.visible = false;
		this.etiqueta.style.opacity = '0';
		const T = this.T;
		const nuevo = await this.crearGrupo(destino).catch(() => null);
		if (!nuevo || !this.vivo) { this.enTransicion = false; return; }

		const viejo = this.actual;
		// dirección del paso en el mundo
		const yawMundo = c ? rad(c.yaw) - viejo.rotation.y : this.yaw;
		const paso = new T.Vector3(-Math.cos(yawMundo), 0, -Math.sin(yawMundo));
		const llegar = c && c.llegar !== undefined ? c.llegar : destino.mirar || 0;
		// la foto nueva se gira para que "llegar" quede justo hacia donde se camina
		nuevo.rotation.y = rad(llegar) - yawMundo;
		this.opacidad(nuevo, 0);
		this.escena.add(nuevo);

		const yaw0 = this.yaw, pitch0 = this.pitch, fov0 = this.camara.fov;
		const giro = difAng(yawMundo, yaw0);
		const avance = c ? Math.min(c.distancia || 2.5, 3.2) : 0;
		const dur = c ? 1.05 : 0.7;
		let t = 0;
		this.velYaw = this.velPitch = 0;

		await new Promise<void>((fin) => {
			const paso1 = (dt: number) => {
				t = Math.min(1, t + dt / dur);
				const e = suave(t);
				// la cámara voltea hacia el destino y "camina"
				this.yaw = yaw0 + giro * Math.min(1, e * 1.6);
				this.pitch = pitch0 * (1 - e);
				viejo.position.copy(paso).multiplyScalar(-avance * e);
				nuevo.position.copy(paso).multiplyScalar(avance * (1 - e) * 0.6);
				this.opacidad(viejo, 1 - clamp((t - 0.25) / 0.6, 0, 1));
				this.opacidad(nuevo, clamp((t - 0.2) / 0.6, 0, 1));
				this.camara.fov = fov0 + Math.sin(e * Math.PI) * (c ? -8 : 0);
				this.camara.updateProjectionMatrix();
				if (t >= 1) { this.animar = null; fin(); }
			};
			this.animar = paso1;
		});

		this.escena.remove(viejo);
		viejo.traverse((o: any) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); });
		nuevo.position.set(0, 0, 0);
		this.opacidad(nuevo, 1);
		this.actual = nuevo;
		this.puntoActual = destino;
		this.camara.fov = this.fov;
		this.camara.updateProjectionMatrix();
		this.enTransicion = false;
		this.precargarVecinos(destino);
		if (this.op.alCambiar) this.op.alCambiar(destino);
	}

	private animar: ((dt: number) => void) | null = null;

	/* ------------------------------ entrada ------------------------------ */

	private eventos(lienzo: HTMLCanvasElement): void {
		const abajo = (e: PointerEvent) => {
			this.arrastre = { x: e.clientX, y: e.clientY, t: performance.now(), mov: 0 };
			lienzo.setPointerCapture(e.pointerId);
			lienzo.style.cursor = 'grabbing';
		};
		const mover = (e: PointerEvent) => {
			const r = lienzo.getBoundingClientRect();
			this.mouse = { x: ((e.clientX - r.left) / r.width) * 2 - 1, y: -((e.clientY - r.top) / r.height) * 2 + 1, dentro: true };
			if (!this.arrastre || this.enTransicion) return;
			const dx = e.clientX - this.arrastre.x, dy = e.clientY - this.arrastre.y;
			this.arrastre.x = e.clientX; this.arrastre.y = e.clientY;
			this.arrastre.mov += Math.abs(dx) + Math.abs(dy);
			const k = (this.camara.fov / r.height) * (Math.PI / 180) * 1.1;
			this.yaw -= dx * k;
			this.pitch = clamp(this.pitch + dy * k, -1.4, 1.4);
			this.velYaw = -dx * k * 60;
			this.velPitch = dy * k * 60;
		};
		const arriba = (e: PointerEvent) => {
			const a = this.arrastre;
			this.arrastre = null;
			lienzo.style.cursor = 'grab';
			if (a && a.mov < 6 && performance.now() - a.t < 500) this.clic();
		};
		const fuera = () => { this.mouse.dentro = false; };
		const rueda = (e: WheelEvent) => {
			e.preventDefault();
			this.fov = clamp(this.fov + e.deltaY * 0.04, 35, 90);
			this.camara.fov = this.fov;
			this.camara.updateProjectionMatrix();
		};
		lienzo.addEventListener('pointerdown', abajo);
		lienzo.addEventListener('pointermove', mover);
		lienzo.addEventListener('pointerup', arriba);
		lienzo.addEventListener('pointerleave', fuera);
		lienzo.addEventListener('wheel', rueda, { passive: false });
		this.quitar.push(() => {
			lienzo.removeEventListener('pointerdown', abajo);
			lienzo.removeEventListener('pointermove', mover);
			lienzo.removeEventListener('pointerup', arriba);
			lienzo.removeEventListener('pointerleave', fuera);
			lienzo.removeEventListener('wheel', rueda);
		});
	}

	/** conexión hacia donde apunta el mouse (marcador o piso en esa dirección) */
	private apuntado(): { conexion: Conexion | null; punto: any | null } {
		const T = this.T;
		this.raycaster.setFromCamera(new T.Vector2(this.mouse.x, this.mouse.y), this.camara);
		const hits = this.raycaster.intersectObjects(this.actual.userData.marcadores, true);
		if (hits.length) return { conexion: hits[0].object.userData.conexion, punto: null };
		const p = new T.Vector3();
		if (!this.raycaster.ray.intersectPlane(this.piso, p) || p.length() > 9) return { conexion: null, punto: null };
		// la conexión más cercana en ángulo a donde se apunta en el piso
		const yawMundo = Math.atan2(-p.z, -p.x);
		let mejor: Conexion | null = null, dmin = rad(32);
		for (const c of this.puntoActual.conexiones) {
			const d = Math.abs(difAng(rad(c.yaw) - this.actual.rotation.y, yawMundo));
			if (d < dmin) { dmin = d; mejor = c; }
		}
		return { conexion: mejor, punto: p };
	}

	private clic(): void {
		if (this.enTransicion) return;
		const a = this.apuntado();
		if (a.conexion) this.irA(a.conexion.a);
	}

	private actualizarCursor(): void {
		if (!this.cursor) return;
		if (!this.mouse.dentro || this.enTransicion || this.arrastre) {
			this.cursor.visible = false;
			this.etiqueta.style.opacity = '0';
			return;
		}
		const a = this.apuntado();
		const T = this.T;
		if (a.punto) {
			this.cursor.visible = true;
			this.cursor.position.set(a.punto.x, -this.alto + 0.015, a.punto.z);
		} else this.cursor.visible = false;
		this.actual.userData.marcadores.forEach((m: any) => m.scale.setScalar(m.userData.conexion === a.conexion ? 1.25 : 1));
		if (a.conexion) {
			const destino = this.punto(a.conexion.a);
			const m = this.actual.userData.marcadores.find((x: any) => x.userData.conexion === a.conexion);
			const v = m.getWorldPosition(new T.Vector3()).project(this.camara);
			if (v.z < 1) {
				this.etiqueta.textContent = destino.titulo;
				this.etiqueta.style.left = ((v.x + 1) / 2) * this.el.clientWidth + 'px';
				this.etiqueta.style.top = ((1 - v.y) / 2) * this.el.clientHeight + 'px';
				this.etiqueta.style.opacity = '1';
			}
			this.renderer.domElement.style.cursor = 'pointer';
		} else {
			this.etiqueta.style.opacity = '0';
			this.renderer.domElement.style.cursor = 'grab';
		}
	}

	/* ------------------------------ ciclo ------------------------------ */

	private redimensionar(): void {
		const w = this.el.clientWidth, h = this.el.clientHeight;
		if (!w || !h) return;
		this.renderer.setSize(w, h, false);
		this.camara.aspect = w / h;
		this.camara.updateProjectionMatrix();
	}

	private bucle = () => {
		if (!this.vivo) return;
		this.raf = requestAnimationFrame(this.bucle);
		const ahora = performance.now();
		const dt = Math.min(0.05, (ahora - this.ultimo) / 1000);
		this.ultimo = ahora;
		if (!this.visible || document.hidden) return;

		if (this.animar) this.animar(dt);
		else if (this.op.cine) {
			this.yaw += dt * 0.045;
			this.pitch = Math.sin(ahora / 9000) * 0.06 - 0.04;
		} else if (!this.arrastre) {
			// inercia al soltar
			this.yaw += this.velYaw * dt;
			this.pitch = clamp(this.pitch + this.velPitch * dt, -1.4, 1.4);
			const f = Math.pow(0.04, dt);
			this.velYaw *= f; this.velPitch *= f;
		}
		this.camara.lookAt(this.dir(this.yaw, this.pitch));
		this.actualizarCursor();
		this.renderer.render(this.escena, this.camara);
	};
}
